import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { FormField } from '@/components/Shared/FormField';
import { formFieldInputClassName } from '@/components/Shared/formFieldStyles';
import { formatWindowHour } from '@/utils/dateUtils';
import {
  POLICY_LIMITS,
  POLICY_TIME_ZONES,
  austrianDefaultForm,
  newTier,
  type BreakTierForm,
  type PolicyErrors,
  type PolicyForm,
} from './timeClockPolicyForm';

interface TimeClockPolicySectionProps {
  form: PolicyForm;
  errors: PolicyErrors;
  onChange: (next: PolicyForm) => void;
}

const START_HOURS = Array.from({ length: 24 }, (_, i) => i);
const END_HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

/** Fields of the tenant's time clock policy; state and saving live in TenantSettingsPage. */
export function TimeClockPolicySection({ form, errors, onChange }: TimeClockPolicySectionProps) {
  const { t } = useTranslation('tenantSettings');
  const k = (key: string) => `timeClockPolicy.${key}`;

  const set = <K extends keyof PolicyForm>(key: K, value: PolicyForm[K]) =>
    onChange({ ...form, [key]: value });

  const updateTier = (key: number, patch: Partial<BreakTierForm>) =>
    set(
      'tiers',
      form.tiers.map((tier) => (tier.key === key ? { ...tier, ...patch } : tier)),
    );

  const timeZones = POLICY_TIME_ZONES.includes(form.timeZoneId) || form.timeZoneId === ''
    ? POLICY_TIME_ZONES
    : [form.timeZoneId, ...POLICY_TIME_ZONES];

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1.5 text-[12px] text-slate-600">
        <p>{t(k('help.rule'))}</p>
        <p className="font-medium text-slate-700">{t(k('help.austria'))}</p>
        <p>{t(k('help.germany'))}</p>
        <div className="pt-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChange(austrianDefaultForm())}
          >
            {t(k('actions.applyAustria'))}
          </Button>
        </div>
      </div>

      <FormField label={t(k('fields.isEnabled'))}>
        <label className="flex items-center gap-2 mt-1 text-[13px] text-slate-700">
          <input
            type="checkbox"
            checked={form.isEnabled}
            onChange={(e) => set('isEnabled', e.target.checked)}
            className="rounded border-slate-300"
          />
          {t(k('hints.isEnabled'))}
        </label>
      </FormField>

      <div className="space-y-2">
        <span className="block text-[12px] font-medium text-slate-600">
          {t(k('fields.breakTiers'))}
        </span>
        {form.tiers.length === 0 && (
          <p className="text-[12px] text-slate-500">{t(k('hints.noTiers'))}</p>
        )}
        {form.tiers.map((tier) => (
          <TierRow
            key={tier.key}
            tier={tier}
            errors={errors.tierErrors?.[tier.key]}
            onChange={(patch) => updateTier(tier.key, patch)}
            onRemove={() => set('tiers', form.tiers.filter((x) => x.key !== tier.key))}
          />
        ))}
        {errors.tiers && <p className="text-[11px] text-red-600">{errors.tiers}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => set('tiers', [...form.tiers, newTier(null, null)])}
            disabled={form.tiers.length >= POLICY_LIMITS.maxTiers}
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t(k('actions.addTier'))}
          </Button>
          <span className="text-[11px] text-slate-500">
            {t(k('hints.breakTiers'), { max: POLICY_LIMITS.maxTiers })}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          label={`${t(k('fields.minBreakSegmentMinutes'))} (${t(k('hints.minutesUnit'))})`}
          type="number"
          inputMode="numeric"
          min={POLICY_LIMITS.minBreakSegmentMin}
          max={POLICY_LIMITS.minBreakSegmentMax}
          value={form.minBreakSegmentMinutes}
          onChange={(e) => set('minBreakSegmentMinutes', e.target.value)}
          error={errors.minBreakSegmentMinutes}
          helperText={t(k('hints.minBreakSegmentMinutes'))}
          className="tabular-nums"
        />
        <FormField
          label={`${t(k('fields.maxWorkHoursPerDay'))} (${t(k('hints.hoursUnit'))})`}
          type="number"
          inputMode="decimal"
          step="0.25"
          min={1}
          max={24}
          value={form.maxWorkHours}
          onChange={(e) => set('maxWorkHours', e.target.value)}
          error={errors.maxWorkHours}
          helperText={t(k('hints.maxWorkHoursPerDay'))}
          className="tabular-nums"
        />
        <FormField label={t(k('fields.stampingStartHour'))} error={errors.stampingWindow}>
          <HourSelect
            hours={START_HOURS}
            value={form.stampingStartHour}
            hasError={!!errors.stampingWindow}
            onChange={(v) => set('stampingStartHour', v)}
          />
        </FormField>
        <FormField
          label={t(k('fields.stampingEndHour'))}
          helperText={t(k('hints.stampingWindow'))}
        >
          <HourSelect
            hours={END_HOURS}
            value={form.stampingEndHour}
            hasError={!!errors.stampingWindow}
            onChange={(v) => set('stampingEndHour', v)}
          />
        </FormField>
        <FormField
          label={t(k('fields.workCountStartHour'))}
          error={errors.workCountStartHour}
          helperText={t(k('hints.workCountStartHour'))}
        >
          <HourSelect
            hours={START_HOURS}
            value={form.workCountStartHour}
            hasError={!!errors.workCountStartHour}
            onChange={(v) => set('workCountStartHour', v)}
          />
        </FormField>
        <FormField
          label={t(k('fields.timeZoneId'))}
          error={errors.timeZoneId}
          helperText={t(k('hints.timeZoneId'))}
        >
          <select
            value={form.timeZoneId}
            onChange={(e) => set('timeZoneId', e.target.value)}
            className={formFieldInputClassName(!!errors.timeZoneId)}
          >
            {form.timeZoneId === '' && <option value="" />}
            {timeZones.map((tz) => (
              <option key={tz} value={tz}>
                {POLICY_TIME_ZONES.includes(tz) ? tz : t(k('currentValue'), { value: tz })}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </div>
  );
}

function TierRow({
  tier,
  errors,
  onChange,
  onRemove,
}: {
  tier: BreakTierForm;
  errors?: { threshold?: string; breakMinutes?: string };
  onChange: (patch: Partial<BreakTierForm>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('tenantSettings');
  const k = (key: string) => `timeClockPolicy.${key}`;
  const numberInput = (hasError: boolean) => formFieldInputClassName(hasError, 'tabular-nums');

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-slate-700">
        <span>{t(k('tier.from'))}</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={24}
          value={tier.hours}
          onChange={(e) => onChange({ hours: e.target.value })}
          aria-label={t(k('tier.hours'))}
          className={`${numberInput(!!errors?.threshold)} !w-16`}
        />
        <span>:</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={59}
          value={tier.minutes}
          onChange={(e) => onChange({ minutes: e.target.value })}
          aria-label={t(k('tier.minutes'))}
          className={`${numberInput(!!errors?.threshold)} !w-16`}
        />
        <span>{t(k('tier.workTime'))} →</span>
        <input
          type="number"
          inputMode="numeric"
          min={POLICY_LIMITS.tierBreakMin}
          max={POLICY_LIMITS.tierBreakMax}
          value={tier.breakMinutes}
          onChange={(e) => onChange({ breakMinutes: e.target.value })}
          aria-label={t(k('tier.breakMinutes'))}
          className={`${numberInput(!!errors?.breakMinutes)} !w-20`}
        />
        <span>
          {t(k('hints.minutesUnit'))} {t(k('tier.breakLabel'))}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          title={t(k('tier.remove'))}
          aria-label={t(k('tier.remove'))}
          className="p-1 sm:p-2"
        >
          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" strokeWidth={2} />
        </Button>
      </div>
      {errors?.threshold && <p className="text-[11px] text-red-600">{errors.threshold}</p>}
      {errors?.breakMinutes && <p className="text-[11px] text-red-600">{errors.breakMinutes}</p>}
    </div>
  );
}

function HourSelect({
  hours,
  value,
  hasError,
  onChange,
}: {
  hours: number[];
  value: string;
  hasError: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={formFieldInputClassName(hasError, 'tabular-nums')}
    >
      {hours.map((hour) => (
        <option key={hour} value={String(hour)}>
          {formatWindowHour(hour)}
        </option>
      ))}
    </select>
  );
}
